import React, {
  useRef,
  useState,
  Dispatch,
  useEffect,
  RefObject,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  Grid,
  Radio,
  MenuItem,
  TextField,
  Typography,
  RadioGroup,
  FormControl,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { IconButton, ListItem, ListItemText } from '@mui/material';

import {
  IdNameModel,
  AlarmMetadata,
  AlarmDefinition,
  DataSourceAlarmMetadata,
} from 'types/types';
import NIService, { INIService } from 'services/NIService';
import { DetailsFormValidationError } from '../CreateForm';
import { capitalizeFirstLetter } from 'components/AlarmManagement/helpers';
import { Ref as AlarmManagementFormRulesRef } from '../FormRules/FormRules';

import Input from '../../../../Common/InputComponents/Input';
import Switch, { SwitchRef } from 'components/Common/InputComponents/Switch';
import Select, { SelectRef } from '../../../../Common/InputComponents/Select';
import { InstancesCreationCriteriaRef } from '../InstancesCreationCriteria/InstancesCreationCriteria';

interface AlarmFormDetailsRef {
  frequency: RefObject<SelectRef>;
  name: RefObject<HTMLInputElement>;
  niDataSource: RefObject<SelectRef>;
  executionPriority: RefObject<SelectRef>;
  severity: RefObject<SeverityElementRef>;
  description: RefObject<HTMLInputElement>;
  executionGroup: RefObject<HTMLInputElement>;
  excludeWeekends: React.RefObject<SwitchRef>;
  alarmExpiration: RefObject<HTMLInputElement>;
}

export interface Ref {
  refs: AlarmFormDetailsRef;
  detailsValidation: Dispatch<SetStateAction<DetailsFormValidationError>>;
}

interface Prop {
  loadingQueryBuilderData: boolean;
  alarmMetaData: AlarmMetadata | undefined;
  alarmDefinitionData: AlarmDefinition | null;
  fetchMetadataForAlarm: (dataSourceId: string) => Promise<void>;
  dataSourceMetadataForAlarm: DataSourceAlarmMetadata | undefined;
  fetchDataProfilesByDataSourceId: (dataSourceId: string) => Promise<void>;
  alarmManagementFormRulesRef: React.MutableRefObject<AlarmManagementFormRulesRef | null>;
  alarmManagementInstancesCreationCriteriaRef: React.MutableRefObject<InstancesCreationCriteriaRef | null>;
}

const FormDetails: ForwardRefRenderFunction<Ref, Prop> = (
  {
    alarmMetaData,
    alarmDefinitionData,
    fetchMetadataForAlarm,
    loadingQueryBuilderData,
    dataSourceMetadataForAlarm,
    alarmManagementFormRulesRef,
    fetchDataProfilesByDataSourceId,
    alarmManagementInstancesCreationCriteriaRef,
  },
  ref
) => {
  const initialErrors: DetailsFormValidationError = {
    name: '',
    frequency: '',
    niDataSource: '',
    executionGroup: '',
    executionPriority: '',
  };

  const [alarmNiDataSourceData, setAlarmNiDataSourceData] =
    useState<IdNameModel[]>();
  const [validationErrors, setValidationErrors] =
    useState<DetailsFormValidationError>(initialErrors);

  const nameRef = useRef<HTMLInputElement>(null);
  const frequencyRef = useRef<SelectRef>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const niDataSourceRef = useRef<SelectRef>(null);
  const executionGroupRef = useRef<HTMLInputElement>(null);
  const alarmExpirationRef = useRef<HTMLInputElement>(null);
  const severityElementRef = useRef<SeverityElementRef>(null);
  const executionPriorityRef = useRef<SelectRef>(null);
  const excludeWeekendsRef = useRef<SwitchRef>(null);

  const refs: AlarmFormDetailsRef = {
    name: nameRef,
    frequency: frequencyRef,
    description: descriptionRef,
    severity: severityElementRef,
    niDataSource: niDataSourceRef,
    executionGroup: executionGroupRef,
    excludeWeekends: excludeWeekendsRef,
    alarmExpiration: alarmExpirationRef,
    executionPriority: executionPriorityRef,
  };

  const fetchAlarmsNiDataSourceData = async () => {
    const niService: INIService = new NIService();
    try {
      niService.GetDataSourceDataForAlarm().then((data) => {
        setAlarmNiDataSourceData(data);
      });
    } catch (error) {
      console.error('Failed to fetch alarm rules count', error);
    }
  };

  const handleOnChange = (field: string) => {
    switch (field.toUpperCase()) {
      case 'ALARMNAME':
        if (validationErrors.name !== '')
          setValidationErrors((prev) => {
            return { ...prev, name: '' };
          });
        break;
      case 'EXECUTIONPRIORITY':
        if (validationErrors.executionPriority !== '')
          setValidationErrors((prev) => {
            return { ...prev, executionPriority: '' };
          });
        break;
      case 'NIDATASOURCE':
        if (validationErrors.niDataSource !== '')
          setValidationErrors((prev) => {
            return { ...prev, niDataSource: '' };
          });
        break;
      case 'FREQUENCY':
        if (validationErrors.frequency !== '')
          setValidationErrors((prev) => {
            return { ...prev, frequency: '' };
          });
        break;
      case 'EXECUTIONGROUP':
        if (validationErrors.executionGroup !== '')
          setValidationErrors((prev) => {
            return { ...prev, executionGroup: '' };
          });
        break;
    }
  };

  const handleChangeDataSourceProvider = (newValue?: string) => {
    fetchMetadataForAlarm(newValue ?? '');
    fetchDataProfilesByDataSourceId(newValue ?? '');
    handleOnChange('niDataSource');
  };

  const handleWeekendsExcludedDisabled = (
    timeAggregationLevel: string | undefined
  ) => {
    if (
      timeAggregationLevel &&
      ['5min', '15min', '30min', 'hourly', 'daily'].includes(
        timeAggregationLevel.toLowerCase()
      )
    ) {
      excludeWeekendsRef.current?.handleDisable(false);
      return false;
    } else {
      excludeWeekendsRef.current?.handleDisable(true);
    }
    excludeWeekendsRef.current?.handleCheckSwitch(false);

    return true;
  };

  const handleFrequencyChange = (value?: string) => {
    handleOnChange('frequency');
    handleWeekendsExcludedDisabled(value);
    alarmManagementInstancesCreationCriteriaRef.current?.breachingPeriodsChecks.current?.handleMaxRetentionNumber(
      value ?? ''
    );

    let showUseDayOverDayToggle = false;
    if (value && !['weekly', 'monthly'].includes(value.toLowerCase())) {
      showUseDayOverDayToggle = true;
    }
    alarmManagementFormRulesRef.current?.refs.studyIntervals?.current?.toggleShowUseDayOverDayToggle(
      showUseDayOverDayToggle
    );
    if (value && ['daily'].includes(value.toLowerCase())) {
      alarmManagementFormRulesRef.current?.refs.busyHour?.current?.toggleShowBusyHourToggle(
        true
      );
    } else if (
      alarmManagementFormRulesRef.current?.refs.busyHour?.current?.isShown
    ) {
      alarmManagementFormRulesRef.current?.refs.busyHour?.current?.toggleShowBusyHourToggle(
        false
      );
    }
  };

  useEffect(() => {
    fetchAlarmsNiDataSourceData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    refs: refs,
    detailsValidation: setValidationErrors,
  }));

  return (
    <div className="alarm-form-card-container">
      <div className="alarm-form-card-title-container">
        <span className="alarm-form-card-title">Details</span>
      </div>
      <div className="alarm-form-elements-container">
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <Typography className="form-element-label">Alarm Name</Typography>
            <Input
              size="small"
              type="text"
              id="alarmName"
              inputRef={nameRef}
              className="input-elements"
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              value={alarmDefinitionData?.name ?? ''}
              handleOnChange={() => handleOnChange('alarmName')}
            />
          </Grid>
          <Grid item xs={5}>
            <Typography className="form-element-label">
              Execution Group
            </Typography>
            <Autocomplete
              freeSolo
              id="executionGroup"
              options={alarmMetaData?.executionGroup ?? []}
              value={alarmDefinitionData?.execution_group ?? ''}
              filterOptions={(options, state) => {
                let filtered = options.filter((option) =>
                  option.toLowerCase().includes(state.inputValue.toLowerCase())
                );
                if (
                  state.inputValue !== '' &&
                  !options.includes(state.inputValue)
                ) {
                  filtered = [state.inputValue, ...filtered];
                }
                return filtered.filter((el) => el.trim() != '');
              }}
              renderOption={(props, option) => {
                const isAddOption =
                  !alarmMetaData?.executionGroup.includes(option);
                return (
                  <ListItem {...props} key={option}>
                    {isAddOption && (
                      <IconButton size="small" disableRipple>
                        <AddIcon />
                      </IconButton>
                    )}
                    <ListItemText
                      primary={
                        isAddOption ? `Add New Group "${option}"` : option
                      }
                    />
                  </ListItem>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  className="input-elements"
                  inputRef={executionGroupRef}
                  error={!!validationErrors.executionGroup}
                  helperText={validationErrors.executionGroup}
                />
              )}
              onChange={() => handleOnChange('executionGroup')}
            />
          </Grid>
          <Grid item xs={5}>
            <Typography className="form-element-label">
              Execution Priority
            </Typography>
            <FormControl fullWidth error={!!validationErrors.executionPriority}>
              <Select
                size="small"
                id="executionPriority"
                className="input-elements"
                inputRef={executionPriorityRef}
                value={alarmDefinitionData?.execution_priority ?? ''}
                handleOnChange={() => handleOnChange('executionPriority')}
              >
                {alarmMetaData?.executionPriority?.map((s) => (
                  <MenuItem key={`Priority-${s.id}`} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.executionPriority}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <Typography className="form-element-label">
              Alarm Expiration
            </Typography>
            <DatePicker
              className="date-picker-element"
              value={
                alarmDefinitionData?.alarm_expiration
                  ? dayjs(alarmDefinitionData.alarm_expiration)
                  : null
              }
              inputRef={alarmExpirationRef}
              name="datePicker"
            />
          </Grid>
          <Grid item xs={10}>
            <Typography className="form-element-label">Description</Typography>
            <Input
              value={alarmDefinitionData?.description ?? ''}
              size="small"
              className="input-elements"
              id="description"
              multiline
              rows={4}
              inputRef={descriptionRef}
            />
          </Grid>
          <Grid item xs={5}>
            <Typography className="form-element-label">
              NI Data Source
            </Typography>
            <FormControl fullWidth error={!!validationErrors.niDataSource}>
              <Select
                size="small"
                id="niDataSource"
                inputRef={niDataSourceRef}
                className="input-elements"
                readOnly={loadingQueryBuilderData}
                value={
                  alarmNiDataSourceData?.find(
                    (s) => s.id === alarmDefinitionData?.dsp_id
                  )?.id ?? ''
                }
                handleOnChange={handleChangeDataSourceProvider}
              >
                {alarmNiDataSourceData?.map((s) => (
                  <MenuItem key={`DataSource-${s.id}`} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{validationErrors.niDataSource}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <AlarmSeverityElement
              alarmDefinitionDataSeverity={
                alarmDefinitionData?.severity ?? null
              }
              alarmMetaData={alarmMetaData}
              ref={severityElementRef}
            />
          </Grid>
          <Grid item xs={5}>
            <Typography className="form-element-label">Frequency</Typography>
            <FormControl fullWidth error={!!validationErrors.frequency}>
              <Select
                size="small"
                disabled={
                  !alarmDefinitionData?.time_aggregation_level &&
                  dataSourceMetadataForAlarm === undefined
                }
                className="input-elements"
                value={alarmDefinitionData?.time_aggregation_level ?? ''}
                inputRef={frequencyRef}
                handleOnChange={handleFrequencyChange}
              >
                {dataSourceMetadataForAlarm?.supportedGranularities.map(
                  (value) => (
                    <MenuItem key={`Frequency-${value}`} value={value}>
                      {capitalizeFirstLetter(value)}
                    </MenuItem>
                  )
                )}
              </Select>
              <FormHelperText>{validationErrors.frequency}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <Typography className="form-element-label">
              Exclude Weekends
            </Typography>
            <FormControl fullWidth className="exclude-weekends-container">
              <Switch
                className={`exclude-weekends-switch`}
                ref={excludeWeekendsRef}
                value={
                  alarmDefinitionData?.execution_rules_serialized
                    ?.weekendsExcluded
                }
                disabled={handleWeekendsExcludedDisabled(
                  alarmDefinitionData?.time_aggregation_level
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default forwardRef<Ref, Prop>(FormDetails);

interface SeverityElementProps {
  alarmMetaData: AlarmMetadata | undefined;
  alarmDefinitionDataSeverity: string | null;
}
interface SeverityRef {
  getSeverity: () => string;
}

interface SeverityElementRef {
  refs: SeverityRef;
}

const SeverityElement: ForwardRefRenderFunction<
  SeverityElementRef,
  SeverityElementProps
> = ({ alarmMetaData, alarmDefinitionDataSeverity }, ref) => {
  const [severity, setSeverity] = useState<string>('Critical');
  const handleSeverityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSeverity(event.target.value);
  };

  const refs: SeverityRef = {
    getSeverity: () => severity,
  };

  useImperativeHandle(ref, () => ({
    refs: refs,
  }));

  useEffect(() => {
    if (alarmDefinitionDataSeverity) {
      setSeverity(alarmDefinitionDataSeverity);
    }
  }, [alarmDefinitionDataSeverity]);

  return (
    <React.Fragment>
      <Typography className="form-element-label">Severity</Typography>
      <div className="radio-buttons-details-container">
        <RadioGroup
          name="severity"
          row
          value={severity}
          onChange={handleSeverityChange}
        >
          {alarmMetaData?.severity?.map((severity) => (
            <FormControlLabel
              key={`Severity-${severity}`}
              value={severity}
              control={<Radio className="radio-button" />}
              label={severity}
            />
          ))}
        </RadioGroup>
      </div>
    </React.Fragment>
  );
};

const AlarmSeverityElement = forwardRef<
  SeverityElementRef,
  SeverityElementProps
>(SeverityElement);
